import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Checkbox, Menu, Divider } from 'antd';
import { ExpandMoreIcon, NewCloseIcon, NewUpdateIcon, ResetIcon } from './Icons';
import NewCustomButton from './NewCustomButton';
import { ButtonTypes } from './CustomButton';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

const CustomDropdownCheckBox = forwardRef(
  (
    {
      className,
      overlayClassName,
      open,
      onClick,
      disabled,
      leftMenuItems = [],
      rightMenuItems = [],
      selectedKeys = [],
      handleChange,
      menuProps = {},
      children,
      showActionButtons = false,
      hideGroupTitles = false,
    },
    ref
  ) => {
    const trans = useSelector(translator);
    const [isVisible, setIsVisible] = useState(open);
    const [tempSelectedKeys, setTempSelectedKeys] = useState(selectedKeys);
    const dropdownRef = useRef(null);
    const clickInsideRef = useRef(false);

    useImperativeHandle(ref, () => ({
      resetDropdown: () => {
        setTempSelectedKeys([]);
        setIsVisible(false);
      },
    }));

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setTempSelectedKeys(selectedKeys); // Reset tempSelectedKeys to original selectedKeys
          setIsVisible(false);
          onClick(event); // Pass event object to onClick callback
        }
      };
      // Only attach the event listener when the dropdown is visible
      if (isVisible) {
        document.addEventListener('click', handleClickOutside);
      }
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [isVisible, selectedKeys, onClick]);

    useEffect(() => {
      if (!isVisible) {
        setTempSelectedKeys(selectedKeys); // Reset tempSelectedKeys when dropdown is closed
      }
    }, [isVisible, selectedKeys]);

    const handleDropdownClick = useCallback((e) => {
      e.stopPropagation();
      if (!clickInsideRef.current) {
        setIsVisible((prev) => !prev);
      }
      clickInsideRef.current = false;
    }, []);

    const handleFilterChange = useCallback(
      (groupKey, itemKey) => {
        const newSelectedKeys = [...tempSelectedKeys];

        if (itemKey === 'non_recc_all' || itemKey === 'recc_all') {
          const groupItems = itemKey === 'non_recc_all' ? leftMenuItems : rightMenuItems;
          const group = groupItems.find((g) => g.key === groupKey);

          if (
            newSelectedKeys.some(
              (selected) => selected.groupKey === groupKey && selected.itemKey === itemKey
            )
          ) {
            group.children.forEach((child) => {
              const index = newSelectedKeys.findIndex(
                (selected) => selected.groupKey === groupKey && selected.itemKey === child.key
              );
              if (index !== -1) {
                newSelectedKeys.splice(index, 1);
              }
            });
          } else {
            group.children.forEach((child) => {
              const index = newSelectedKeys.findIndex(
                (selected) => selected.groupKey === groupKey && selected.itemKey === child.key
              );
              if (index === -1) {
                newSelectedKeys.push({ groupKey, itemKey: child.key });
              }
            });
          }
        } else {
          const index = newSelectedKeys.findIndex(
            (selected) => selected.groupKey === groupKey && selected.itemKey === itemKey
          );

          if (index === -1) {
            newSelectedKeys.push({ groupKey, itemKey });
          } else {
            newSelectedKeys.splice(index, 1);
          }
        }

        const groupItems = groupKey === 'status' ? leftMenuItems : rightMenuItems;
        const group = groupItems.find((g) => g.key === groupKey);
        const allSelected = group.children.every((child) =>
          newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === child.key
          )
        );

        const allKey = groupKey === 'status' ? 'non_recc_all' : 'recc_all';
        if (allSelected) {
          if (
            !newSelectedKeys.some(
              (selected) => selected.groupKey === groupKey && selected.itemKey === allKey
            )
          ) {
            newSelectedKeys.push({ groupKey, itemKey: allKey });
          }
        } else {
          const allIndex = newSelectedKeys.findIndex(
            (selected) => selected.groupKey === groupKey && selected.itemKey === allKey
          );
          if (allIndex !== -1) {
            newSelectedKeys.splice(allIndex, 1);
          }
        }

        // Additional logic for auto-select/deselect based on specific conditions
        if (groupKey === 'status') {
          const ongoingSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'ongoing'
          );
          const scheduledSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'scheduled'
          );
          const completedSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'completed'
          );
          const expiredSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'expired'
          );

          if (ongoingSelected && scheduledSelected && completedSelected && expiredSelected) {
            if (
              !newSelectedKeys.some(
                (selected) => selected.groupKey === groupKey && selected.itemKey === 'non_recc_all'
              )
            ) {
              newSelectedKeys.push({ groupKey, itemKey: 'non_recc_all' });
            }
          } else {
            const nonReccAllIndex = newSelectedKeys.findIndex(
              (selected) => selected.groupKey === groupKey && selected.itemKey === 'non_recc_all'
            );
            if (nonReccAllIndex !== -1) {
              newSelectedKeys.splice(nonReccAllIndex, 1);
            }
          }
        } else if (groupKey === 'recurrence') {
          const onetimeSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'recc_onetime'
          );
          const dailySelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'daily'
          );
          const weeklySelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'weekly'
          );
          const customSelected = newSelectedKeys.some(
            (selected) => selected.groupKey === groupKey && selected.itemKey === 'custom'
          );

          if (onetimeSelected && dailySelected && weeklySelected && customSelected) {
            if (
              !newSelectedKeys.some(
                (selected) => selected.groupKey === groupKey && selected.itemKey === 'recc_all'
              )
            ) {
              newSelectedKeys.push({ groupKey, itemKey: 'recc_all' });
            }
          } else {
            const reccAllIndex = newSelectedKeys.findIndex(
              (selected) => selected.groupKey === groupKey && selected.itemKey === 'recc_all'
            );
            if (reccAllIndex !== -1) {
              newSelectedKeys.splice(reccAllIndex, 1);
            }
          }
        }

        setTempSelectedKeys(newSelectedKeys);
      },
      [tempSelectedKeys, leftMenuItems, rightMenuItems]
    );

    const handleItemClick = (groupKey, itemKey) => {
      if (showActionButtons) {
        handleFilterChange(groupKey, itemKey);
        return;
      }
      handleChange(groupKey, itemKey);
    };

    const renderMenu = (items) => (
      <Menu>
        {items.map((group) => (
          <Menu.ItemGroup key={group.key} title={hideGroupTitles ? null : group.label}>
            {group.children.map((item) => (
              <Menu.Item
                key={item.key}
                onClick={() => handleItemClick(group.key, item.key)}
                className={
                  (showActionButtons ? tempSelectedKeys : selectedKeys).some(
                    (selected) => selected.groupKey === group.key && selected.itemKey === item.key
                  )
                    ? 'highlight-label'
                    : 'non-highlighted-label'
                }
              >
                <Checkbox
                  onClick={(e) => e.preventDefault()}
                  checked={(showActionButtons ? tempSelectedKeys : selectedKeys).some(
                    (selected) => selected.groupKey === group.key && selected.itemKey === item.key
                  )}
                >
                  <span>{item.label}</span>
                </Checkbox>
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        ))}
      </Menu>
    );

    const handleAction = (action, closeDropdown) => {
      if (action === 'reset') {
        setTempSelectedKeys([]);
        handleChange(action, tempSelectedKeys, closeDropdown);
      } else if (action === 'cancel') {
        handleChange(action, selectedKeys, closeDropdown);
      } else if (action === 'apply') {
        handleChange(action, tempSelectedKeys, closeDropdown);
      }
    };

    return (
      <Dropdown
        overlay={
          <div style={{ padding: 10 }} className={className}>
            <div className={`${showActionButtons ? 'leftrightmenu' : 'leftmenuonly'}`}>
              <div>{renderMenu(leftMenuItems)}</div>
              <div>{renderMenu(rightMenuItems)}</div>
            </div>
            {showActionButtons && (
              <div className="dropdownbuttons">
                <div
                  className="buttonstyle reset-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('reset', () => setIsVisible(false));
                  }}
                >
                  <ResetIcon />
                  <span>{trans('RESETFILTERS')}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginRight: '-25px',
                  }}
                >
                  <NewCustomButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction('cancel', () => setIsVisible(false));
                    }}
                    className="cancel"
                    label={trans('CANCEL')}
                    icon={
                      <NewCloseIcon CheckboxclassName="cancel-icon" style={{ fill: '#4E758E' }} />
                    }
                  />
                  <NewCustomButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction('apply', () => setIsVisible(false));
                    }}
                    type={ButtonTypes.primary}
                    label={trans('APPLY')}
                    icon={
                      <NewUpdateIcon
                        style={{ fill: '#003252' }}
                        className={'update-icon-enabled'}
                      />
                    }
                  />
                </div>
              </div>
            )}
          </div>
        }
        visible={isVisible}
        disabled={disabled}
        trigger={['click']}
        {...menuProps}
      >
        <div
          ref={dropdownRef}
          className={`custom-dropdown ${className ?? ''} ${disabled ? 'disabled' : ''}`}
          onClick={handleDropdownClick}
        >
          <div className="dropdown-label">{children}</div>
          {isVisible ? (
            <ExpandMoreIcon className="status-icon rotate-180" />
          ) : (
            <ExpandMoreIcon className="status-icon" />
          )}
        </div>
      </Dropdown>
    );
  }
);

CustomDropdownCheckBox.propTypes = {
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  open: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  leftMenuItems: PropTypes.arrayOf(PropTypes.object),
  rightMenuItems: PropTypes.arrayOf(PropTypes.object),
  selectedKeys: PropTypes.arrayOf(PropTypes.object),
  handleChange: PropTypes.func,
  menuProps: PropTypes.object,
  children: PropTypes.node.isRequired,
  hideGroupTitles: PropTypes.bool,
};

export default CustomDropdownCheckBox;
